import React, { useEffect, useMemo, useState } from "react";
import {
  CreditCard, Search, Users, RefreshCw, ChevronDown,
  CheckCircle2, XCircle, Trash2, Building2, KeyRound, Quote,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { apiDelete, apiGet, apiPost, apiPut } from "../../utils/api";
import { toast } from "../../utils/toast";
import "./admin-shell.css";

/* ── helpers ── */
function normalizeText(v) { return String(v || "").trim(); }
function normalizeSeat(v) { return String(v || "").replace(/[^\d]/g, "").slice(0, 6); }
function fmt(v) {
  const t = new Date(v).getTime();
  if (!Number.isFinite(t)) return "-";
  return new Intl.DateTimeFormat("en-IN", { dateStyle: "medium", timeStyle: "short" }).format(new Date(t));
}
const PAISE = 100;
function paiseToUnits(v) { const n = Number(v); return (Number.isFinite(n) && n > 0) ? (n / PAISE).toFixed(2) : ""; }
function sanitizeCurrency(v) { return String(v || "").toUpperCase().replace(/[^A-Z]/g, "").slice(0, 3); }

/* ════════════════════════════════════════
   Overview tab
   ════════════════════════════════════════ */
function OverviewTab({ tenant, onToggle, onDelete, onRefresh }) {
  const [seatInput, setSeatInput] = useState(
    String(tenant?.maxSeats === null ? "" : Number(tenant?.maxSeats || 5))
  );
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSeatInput(String(tenant?.maxSeats === null ? "" : Number(tenant?.maxSeats || 5)));
  }, [tenant?.id, tenant?.maxSeats]);

  const saveSeats = async () => {
    if (saving) return;
    const n = Number.parseInt(seatInput.trim(), 10);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Enter a valid seat count"); return; }
    setSaving(true);
    try {
      const res = await apiPost(`/api/admin/tenants/${encodeURIComponent(tenant.id)}/seats`, { maxSeats: n });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d?.message || "Failed to update seats"); return; }
      toast.success("Seat limit updated");
      onRefresh();
    } catch { toast.error("Failed to update seats"); }
    finally { setSaving(false); }
  };

  return (
    <div className="atw-overview">
      {/* Key facts table */}
      <div className="admin-table-wrap">
        <table className="admin-table atw-kv-table">
          <tbody>
            <tr>
              <th>Tenant Name</th><td>{tenant.name || "-"}</td>
              <th>Owner Email</th><td>{tenant.ownerEmail || "-"}</td>
            </tr>
            <tr>
              <th>Enrollment Key</th>
              <td className="admin-monospace">{tenant.enrollmentKey || "-"}</td>
              <th>Status</th>
              <td>
                <span className={`admin-status ${tenant.isActive ? "is-active" : "is-disabled"}`}>
                  {tenant.isActive ? "Active" : "Disabled"}
                </span>
              </td>
            </tr>
            <tr>
              <th>Registered Devices</th><td>{Number(tenant.deviceCount || 0)}</td>
              <th>Seat Cap</th>
              <td>{tenant.maxSeats === null ? "Unlimited" : Number(tenant.maxSeats || 5)}</td>
            </tr>
            {tenant.activePlanName && (
              <tr>
                <th>Active Plan</th>
                <td><span className="admin-chip">{tenant.activePlanName}</span></td>
                <th></th><td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Controls */}
      <div className="atw-controls">
        <div className="atw-seat-row">
          <span className="atw-control-label">Adjust Seat Cap</span>
          <input
            className="admin-input atw-seat-input"
            placeholder="Seat limit"
            value={seatInput}
            onChange={e => setSeatInput(normalizeSeat(e.target.value))}
          />
          <button className="admin-btn" onClick={saveSeats} disabled={saving}>
            {saving ? "Saving…" : "Save Seats"}
          </button>
        </div>
        <div className="atw-action-row">
          <button
            className={tenant.isActive ? "admin-btn-secondary" : "admin-btn"}
            onClick={() => onToggle(tenant)}
          >
            {tenant.isActive ? "Deactivate Workspace" : "Activate Workspace"}
          </button>
          <button className="admin-btn-danger" onClick={() => onDelete(tenant)}>
            <Trash2 size={14} /> Delete Tenant
          </button>
        </div>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   Licenses tab (per-tenant)
   ════════════════════════════════════════ */
function LicensesTab({ tenantId }) {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/api/admin/tenants/${encodeURIComponent(tenantId)}/agents`);
      const d = await res.json().catch(() => []);
      if (!res.ok) throw new Error(d?.message || "Failed");
      setAgents(Array.isArray(d) ? d : []);
    } catch (e) { toast.error(e?.message || "Failed to load agents"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [tenantId]);

  const filtered = useMemo(() => {
    const q = normalizeText(search).toLowerCase();
    return agents.filter(a => {
      const matchQ = !q || [a?.agentId, a?.hostname, a?.name, a?.ip].some(v => normalizeText(v).toLowerCase().includes(q));
      const matchS = statusFilter === "all"
        || (statusFilter === "licensed" && !!a?.isLicensed)
        || (statusFilter === "pending" && !a?.isLicensed);
      return matchQ && matchS;
    });
  }, [agents, search, statusFilter]);

  const approve = async (id) => {
    const res = await apiPost(`/api/admin/agents/${id}/approve`);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(d?.message || "Failed"); return; }
    toast.success("Agent approved"); void load();
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this license?")) return;
    const res = await apiPost(`/api/admin/agents/${id}/deactivate`);
    if (!res.ok) { toast.error("Revoke failed"); return; }
    toast.success("License revoked"); void load();
  };

  const del = async (agent) => {
    if (!window.confirm(`Delete agent "${agent.agentId}" and all telemetry? This cannot be undone.`)) return;
    const res = await apiDelete(`/api/admin/agents/${agent._id}`);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(d?.message || "Delete failed"); return; }
    toast.success("Agent deleted"); void load();
  };

  const licensed = agents.filter(a => a?.isLicensed).length;

  return (
    <div>
      {/* Stats strip */}
      <div className="atw-stat-strip">
        <div className="atw-stat"><span>Licensed</span><strong>{licensed}</strong></div>
        <div className="atw-stat"><span>Registered</span><strong>{agents.length}</strong></div>
        <div className="atw-stat"><span>Pending</span><strong>{Math.max(0, agents.length - licensed)}</strong></div>
        <button className="gc-icon-btn atw-refresh-btn" onClick={load} data-tip="Refresh" title="Refresh">
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Filters */}
      <div className="admin-toolbar">
        <label className="admin-search" style={{ flex: "1 1 340px" }}>
          <Search size={14} />
          <input
            type="search"
            placeholder="Search agent ID, hostname, IP…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </label>
        <select className="admin-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All endpoints</option>
          <option value="licensed">Licensed only</option>
          <option value="pending">Pending only</option>
        </select>
        <span className="admin-panel-kicker">{filtered.length} visible</span>
      </div>

      {loading ? (
        <div className="admin-empty-state">Loading agents…</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty-state">No endpoints match the current filters.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Agent ID</th>
                <th>Hostname</th>
                <th>IP Address</th>
                <th>Fingerprint</th>
                <th>Status</th>
                <th>Last Sync</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(a => (
                <tr key={a._id}>
                  <td><div className="admin-cell-title admin-monospace">{a.agentId || "-"}</div></td>
                  <td><div className="admin-cell-title">{a.hostname || a.name || "-"}</div></td>
                  <td><div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{a.ip || "-"}</div></td>
                  <td>
                    <span className="admin-monospace" style={{ fontSize: "0.78rem" }}>
                      {a.fingerprint ? a.fingerprint.slice(0, 16).toUpperCase() : "UNBOUND"}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-status ${a.isLicensed ? "is-provisioned" : "is-pending"}`}>
                      {a.isLicensed ? "Licensed" : "Pending"}
                    </span>
                  </td>
                  <td><div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{fmt(a.lastSeen)}</div></td>
                  <td>
                    <div className="admin-actions">
                      {a.isLicensed
                        ? <button className="admin-inline-action" onClick={() => revoke(a._id)}>Revoke</button>
                        : <button className="admin-inline-action" onClick={() => approve(a._id)}>
                            <CheckCircle2 size={13} /> Approve
                          </button>
                      }
                      <button className="admin-inline-action atw-danger-action" onClick={() => del(a)}>
                        <Trash2 size={13} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════
   Billing tab (per-tenant) — minimal tables
   ════════════════════════════════════════ */
function BillingTab({ tenant }) {
  const tenantId = tenant?.id;
  const navigate  = useNavigate();
  const [subscriptions, setSubscriptions] = useState([]);
  const [payments, setPayments] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [plans, setPlans] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [billingTab, setBillingTab] = useState("subscriptions");
  const [form, setForm] = useState({
    planId: "", billingCycle: "monthly",
    paymentMode: "admin_granted", seatCount: "", notes: "",
  });

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [subsRes, payRes, invRes, planRes] = await Promise.all([
        apiGet(`/api/admin/billing/subscriptions?tenantId=${encodeURIComponent(tenantId)}&limit=100`),
        apiGet(`/api/admin/billing/payments?tenantId=${encodeURIComponent(tenantId)}&limit=100`),
        apiGet(`/api/admin/billing/invoices?tenantId=${encodeURIComponent(tenantId)}&limit=100`),
        apiGet("/api/admin/plan-pricing"),
      ]);
      const [subs, pays, invs, planPayload] = await Promise.all([
        subsRes.json().catch(() => null),
        payRes.json().catch(() => null),
        invRes.json().catch(() => null),
        planRes.json().catch(() => null),
      ]);
      if (!subsRes.ok) throw new Error(subs?.message || "Failed to fetch subscriptions");
      if (!payRes.ok)  throw new Error(pays?.message  || "Failed to fetch payments");
      if (!invRes.ok)  throw new Error(invs?.message  || "Failed to fetch invoices");
      if (!planRes.ok) throw new Error(planPayload?.message || "Failed to fetch plans");
      const catalog = Array.isArray(planPayload?.catalog) ? planPayload.catalog : [];
      setSubscriptions(Array.isArray(subs?.subscriptions) ? subs.subscriptions : []);
      setPayments(Array.isArray(pays?.payments) ? pays.payments : []);
      setInvoices(Array.isArray(invs?.invoices) ? invs.invoices : []);
      setPlans(catalog);
      setForm(p => ({ ...p, planId: p.planId || String(catalog[0]?.id || "") }));
    } catch (e) { toast.error(e?.message || "Failed to load billing"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [tenantId]);

  const grantTier = async (e) => {
    e.preventDefault();
    if (saving || !tenantId || !form.planId) { toast.error("Select a plan"); return; }
    const payload = {
      planId: form.planId, billingCycle: form.billingCycle,
      paymentMode: form.paymentMode || "admin_granted",
      notes: normalizeText(form.notes),
    };
    const seats = Number.parseInt(form.seatCount, 10);
    if (Number.isFinite(seats) && seats > 0) payload.seatCount = seats;
    setSaving(true);
    try {
      const res = await apiPost(`/api/admin/billing/tenants/${encodeURIComponent(tenantId)}/grant-tier`, payload);
      const d = await res.json().catch(() => null);
      if (!res.ok || !d?.success) { toast.error(d?.message || "Failed to grant tier"); return; }
      toast.success("Tier granted"); void load();
    } catch { toast.error("Failed to grant tier"); }
    finally { setSaving(false); }
  };

  const goDetail = (type, r) =>
    navigate(`/admin/tenants/${tenantId}/billing/${type}/${r.id}`, {
      state: { record: r, tenantName: tenant?.name, tenantId },
    });

  return (
    <div>
      {/* Grant tier form */}
      <div className="admin-panel atw-grant-form">
        <div className="admin-panel-head">
          <h3 className="admin-panel-title">Grant / Change Tier</h3>
        </div>
        <form onSubmit={grantTier}>
          <div className="atw-grant-grid">
            <div className="admin-form-field">
              <label>Plan</label>
              <select className="admin-input" value={form.planId} onChange={e => setForm(p => ({ ...p, planId: e.target.value }))}>
                {plans.map(pl => <option key={pl.id} value={pl.id}>{pl.name || pl.id}</option>)}
              </select>
            </div>
            <div className="admin-form-field">
              <label>Billing Cycle</label>
              <select className="admin-input" value={form.billingCycle} onChange={e => setForm(p => ({ ...p, billingCycle: e.target.value }))}>
                <option value="monthly">Monthly</option>
                <option value="yearly">Yearly</option>
                <option value="one_time">One Time</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label>Payment Mode</label>
              <select className="admin-input" value={form.paymentMode} onChange={e => setForm(p => ({ ...p, paymentMode: e.target.value }))}>
                <option value="admin_granted">Admin Grant (No Payment)</option>
                <option value="bank_transfer">Bank Transfer</option>
                <option value="upi">UPI</option>
                <option value="cash">Cash</option>
                <option value="razorpay_collected_by_admin">Razorpay by Admin</option>
              </select>
            </div>
            <div className="admin-form-field">
              <label>Seat Count</label>
              <input className="admin-input" placeholder="e.g. 10" value={form.seatCount}
                onChange={e => setForm(p => ({ ...p, seatCount: normalizeSeat(e.target.value) }))} />
            </div>
            <div className="admin-form-field" style={{ gridColumn: "span 2" }}>
              <label>Admin Note</label>
              <input className="admin-input" placeholder="Optional note" value={form.notes}
                onChange={e => setForm(p => ({ ...p, notes: e.target.value.slice(0, 500) }))} />
            </div>
          </div>
          <div className="admin-actions" style={{ marginTop: 12 }}>
            <button type="submit" className="admin-btn" disabled={saving}>
              {saving ? "Granting…" : "Grant Tier"}
            </button>
          </div>
        </form>
      </div>

      {/* Billing records — minimal tables, click row for full detail */}
      <div className="admin-tabbar" style={{ marginTop: 16 }}>
        {[
          { id: "subscriptions", label: `Subscriptions (${subscriptions.length})` },
          { id: "payments",      label: `Payments (${payments.length})` },
          { id: "invoices",      label: `Invoices (${invoices.length})` },
        ].map(t => (
          <button key={t.id} type="button"
            className={`admin-tab${billingTab === t.id ? " is-active" : ""}`}
            onClick={() => setBillingTab(t.id)}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-empty-state">Loading billing records…</div>
      ) : billingTab === "subscriptions" ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Plan</th><th>Status</th><th>Expires</th></tr>
            </thead>
            <tbody>
              {subscriptions.length === 0
                ? <tr><td colSpan={3} className="atw-empty-row">No subscriptions found.</td></tr>
                : subscriptions.map(r => (
                  <tr key={r.id} className="atw-clickable-row" onClick={() => goDetail("subscription", r)}>
                    <td><div className="admin-cell-title">{r.planName || r.planId || "-"}</div></td>
                    <td>
                      <span className={`admin-status ${r.status === "active" ? "is-active" : r.status === "expired" ? "is-disabled" : "is-pending"}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td><div className="admin-cell-subtitle atw-cell-muted">{r.expiresAt ? fmt(r.expiresAt) : "—"}</div></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      ) : billingTab === "payments" ? (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Amount</th><th>Status</th><th>Date</th></tr>
            </thead>
            <tbody>
              {payments.length === 0
                ? <tr><td colSpan={3} className="atw-empty-row">No payment records.</td></tr>
                : payments.map(r => (
                  <tr key={r.id} className="atw-clickable-row" onClick={() => goDetail("payment", r)}>
                    <td><div className="admin-cell-title">₹{Number(r.totalAmount || 0).toLocaleString("en-IN")}</div></td>
                    <td>
                      <span className={`admin-status ${r.status === "paid" ? "is-active" : "is-pending"}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td><div className="admin-cell-subtitle atw-cell-muted">{r.createdAt ? fmt(r.createdAt) : "—"}</div></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr><th>Invoice #</th><th>Status</th><th>Total</th></tr>
            </thead>
            <tbody>
              {invoices.length === 0
                ? <tr><td colSpan={3} className="atw-empty-row">No invoices found.</td></tr>
                : invoices.map(r => (
                  <tr key={r.id} className="atw-clickable-row" onClick={() => goDetail("invoice", r)}>
                    <td><div className="admin-cell-title admin-monospace">{r.invoiceNumber || r.id || "-"}</div></td>
                    <td>
                      <span className={`admin-status ${r.status === "paid" ? "is-active" : "is-pending"}`}>
                        {r.status || "-"}
                      </span>
                    </td>
                    <td><div className="admin-cell-subtitle atw-cell-muted">₹{Number(r.total || 0).toLocaleString("en-IN")}</div></td>
                  </tr>
                ))
              }
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════
   Users tab (per-tenant)
   ════════════════════════════════════════ */
function UsersTab({ tenantId }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [resetTarget, setResetTarget] = useState(null); // { id, name, email }
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/api/admin/tenants/${encodeURIComponent(tenantId)}/users`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.message || "Failed to load users");
      setUsers(Array.isArray(d?.users) ? d.users : []);
    } catch (e) { toast.error(e?.message || "Failed to load users"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [tenantId]);

  const filtered = useMemo(() => {
    const q = normalizeText(search).toLowerCase();
    if (!q) return users;
    return users.filter(u =>
      [u?.name, u?.email, u?.role].some(v => normalizeText(v).toLowerCase().includes(q))
    );
  }, [users, search]);

  const openReset = (u) => {
    setResetTarget({ id: u._id, name: u.name, email: u.email });
    setNewPassword("");
  };
  const closeReset = () => { if (resetting) return; setResetTarget(null); setNewPassword(""); };

  const submitReset = async () => {
    if (!newPassword || newPassword.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (!resetTarget) return;
    setResetting(true);
    try {
      const res = await apiPut(
        `/api/admin/tenants/${encodeURIComponent(tenantId)}/users/${encodeURIComponent(resetTarget.id)}/reset-password`,
        { newPassword }
      );
      const d = await res.json().catch(() => ({}));
      if (!res.ok) { toast.error(d?.message || "Failed to reset password"); return; }
      toast.success(`Password reset for ${resetTarget.name || resetTarget.email}`);
      closeReset();
    } catch { toast.error("Network error while resetting password"); }
    finally { setResetting(false); }
  };

  return (
    <div>
      <div className="admin-toolbar" style={{ marginBottom: 12 }}>
        <label className="admin-search" style={{ flex: "1 1 340px" }}>
          <Search size={14} />
          <input
            type="search"
            placeholder="Search by name, email, or role…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </label>
        <span className="admin-panel-kicker">{filtered.length} users</span>
        <button className="gc-icon-btn" data-tip="Refresh" onClick={load}>
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="admin-empty-state">Loading users…</div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty-state">No users found{search ? " matching the search" : " for this tenant"}.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Status</th>
                <th>Last Login</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(u => (
                <tr key={u._id}>
                  <td><div className="admin-cell-title">{u.name || "-"}</div></td>
                  <td><div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{u.email || "-"}</div></td>
                  <td><span className="admin-chip">{u.role || "-"}</span></td>
                  <td>
                    <span className={`admin-status ${u.isActive !== false ? "is-active" : "is-disabled"}`}>
                      {u.isActive !== false ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td><div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{u.lastLogin ? fmt(u.lastLogin) : "Never"}</div></td>
                  <td>
                    <div className="admin-actions">
                      <button
                        className="admin-inline-action"
                        onClick={() => openReset(u)}
                      >
                        <KeyRound size={13} /> Reset Password
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Reset Password inline modal */}
      {resetTarget && (
        <div className="atw-modal-backdrop" onClick={closeReset}>
          <div className="atw-modal" onClick={e => e.stopPropagation()}>
            <div className="atw-modal-head">
              <div>
                <div className="atw-modal-title">Reset Password</div>
                <div className="atw-modal-sub">{resetTarget.name || resetTarget.email}</div>
              </div>
              <button className="admin-btn-icon" onClick={closeReset} disabled={resetting} aria-label="Close">
                <XCircle size={16} />
              </button>
            </div>
            <div className="atw-modal-body">
              <div className="admin-form-field">
                <label className="admin-form-label">New Password</label>
                <input
                  type="password"
                  className="admin-input"
                  placeholder="Minimum 6 characters"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  disabled={resetting}
                  autoFocus
                  onKeyDown={e => { if (e.key === "Enter") submitReset(); }}
                />
              </div>
            </div>
            <div className="atw-modal-foot">
              <button className="admin-btn-secondary" onClick={closeReset} disabled={resetting}>Cancel</button>
              <button className="admin-btn" onClick={submitReset} disabled={resetting}>
                {resetting ? "Resetting…" : "Reset Password"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ════════════════════════════════════════
   Enterprise Quote tab (per-tenant)
   ════════════════════════════════════════ */
function EnterpriseQuoteTab({ tenantId }) {
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ amount: "", seatCount: "", currency: "INR", isActive: false, notes: "" });

  const load = async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const res = await apiGet(`/api/admin/contact-sales/enterprise-quotes?tenantId=${encodeURIComponent(tenantId)}`);
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d?.message || "Failed to load quote");
      const quotes = Array.isArray(d?.quotes) ? d.quotes : [];
      const found = quotes.find(q => String(q?.tenantId?._id || q?.tenantId || "") === String(tenantId)) || null;
      setQuote(found);
      setForm({
        amount: found ? paiseToUnits(found.priceInPaise) : "",
        seatCount: found?.seatCount ? String(found.seatCount) : "",
        currency: found?.currency || "INR",
        isActive: !!found?.isActive,
        notes: found?.notes || "",
      });
    } catch (e) { toast.error(e?.message || "Failed to load enterprise quote"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [tenantId]);

  const unitPreview = useMemo(() => {
    const amt = Number(form.amount);
    const seats = Number.parseInt(form.seatCount, 10);
    if (!Number.isFinite(amt) || amt <= 0 || !Number.isFinite(seats) || seats <= 0) return null;
    return (Math.round((amt * PAISE) / seats) / PAISE).toFixed(2);
  }, [form.amount, form.seatCount]);

  const save = async () => {
    const currency = sanitizeCurrency(form.currency || "INR");
    if (!/^[A-Z]{3}$/.test(currency)) { toast.error("Currency must be a valid 3-letter code"); return; }
    const amount = Number(form.amount);
    const seatCount = form.seatCount ? Number.parseInt(form.seatCount, 10) : NaN;
    if (form.isActive) {
      if (!Number.isFinite(amount) || amount <= 0) { toast.error("Active quote must have an amount > 0"); return; }
      if (!Number.isFinite(seatCount) || seatCount <= 0) { toast.error("Active quote must include a positive seat count"); return; }
    }
    const priceInPaise = Number.isFinite(amount) && amount > 0 ? Math.round(amount * PAISE) : 0;

    setSaving(true);
    try {
      const res = await apiPut(`/api/admin/contact-sales/enterprise-quotes/${encodeURIComponent(tenantId)}`, {
        priceInPaise,
        seatCount: Number.isFinite(seatCount) && seatCount > 0 ? seatCount : null,
        currency,
        isActive: !!form.isActive,
        notes: String(form.notes || "").trim(),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok || !d?.quote) { toast.error(d?.message || "Failed to save quote"); return; }
      toast.success("Enterprise quote saved");
      void load();
    } catch { toast.error("Network error while saving quote"); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="admin-empty-state">Loading enterprise quote…</div>;

  return (
    <div className="atw-quote-wrap">
      <div className="atw-quote-head-row">
        <div>
          <p className="atw-quote-desc">
            Set the negotiated subtotal and locked seat count for this tenant's enterprise checkout. GST is added automatically at checkout.
          </p>
        </div>
        <span className={`admin-status ${form.isActive ? "is-active" : "is-disabled"}`}>
          {form.isActive ? "Payment Enabled" : "Payment Disabled"}
        </span>
      </div>

      <div className="atw-quote-grid">
        <div className="admin-form-field">
          <label className="admin-form-label">Negotiated Subtotal</label>
          <input
            type="number" min="0" step="0.01"
            className="admin-input"
            value={form.amount}
            onChange={e => setForm(p => ({ ...p, amount: e.target.value }))}
            disabled={saving}
          />
        </div>
        <div className="admin-form-field">
          <label className="admin-form-label">Quoted Seats</label>
          <input
            type="text" inputMode="numeric"
            className="admin-input"
            value={form.seatCount}
            onChange={e => setForm(p => ({ ...p, seatCount: normalizeSeat(e.target.value) }))}
            disabled={saving}
          />
        </div>
        <div className="admin-form-field">
          <label className="admin-form-label">Currency</label>
          <input
            type="text" maxLength={3}
            className="admin-input atw-currency-input"
            value={form.currency}
            onChange={e => setForm(p => ({ ...p, currency: sanitizeCurrency(e.target.value) }))}
            disabled={saving}
          />
        </div>
      </div>

      {unitPreview !== null && (
        <div className="atw-quote-info">
          Per agent before GST: <strong>{unitPreview} {form.currency || "INR"}</strong>
        </div>
      )}
      {quote?.seatCount && (
        <div className="atw-quote-info">
          Saved quote: <strong>{quote.seatCount} seats</strong> @ <strong>{(Number(quote.unitPriceInPaise || 0) / PAISE).toFixed(2)} {quote.currency}</strong> each
          {quote.updatedAt && <span className="atw-quote-meta-dim"> · Updated {fmt(quote.updatedAt)}</span>}
        </div>
      )}

      <label className="atw-quote-toggle-row">
        <input
          type="checkbox"
          checked={!!form.isActive}
          onChange={e => setForm(p => ({ ...p, isActive: e.target.checked }))}
          disabled={saving}
        />
        Enable tenant checkout (Razorpay)
      </label>

      <div className="admin-form-field" style={{ marginTop: 10 }}>
        <label className="admin-form-label">Internal Note</label>
        <textarea
          className="admin-input atw-quote-note"
          rows={2}
          value={form.notes}
          onChange={e => setForm(p => ({ ...p, notes: e.target.value }))}
          disabled={saving}
        />
      </div>

      <div className="admin-actions" style={{ marginTop: 14 }}>
        <button className="admin-btn" onClick={save} disabled={saving}>
          {saving ? "Saving…" : "Save Enterprise Quote"}
        </button>
        <button className="gc-icon-btn" data-tip="Reload" onClick={load} disabled={saving || loading}>
          <RefreshCw size={14} />
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════
   Shared scoped styles (used by both views)
   ════════════════════════════════════════ */
const ATW_STYLES = `
  .atw-pills {
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
  }
  .atw-pill {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border-radius: 4px;
    border: 1px solid var(--as-border);
    background: var(--as-surface);
    font-size: 0.8rem;
  }
  .atw-pill span { color: var(--as-text-2); }
  .atw-pill strong { color: var(--as-text); font-weight: 700; }
  .atw-pill--success { border-color: rgba(16,124,16,.25); background: var(--as-success-bg); }
  .atw-pill--success span { color: var(--as-success); }
  .atw-pill--success strong { color: var(--as-success); }
  .atw-pill--danger { border-color: rgba(164,38,44,.25); background: var(--as-danger-bg); }
  .atw-pill--danger span { color: var(--as-danger); }
  .atw-pill--danger strong { color: var(--as-danger); }

  .atw-main-table { min-width: 900px; }

  .atw-clickable-row { cursor: pointer; }
  .atw-clickable-row:hover td { background: var(--as-bg) !important; }
  .atw-empty-row { text-align: center; color: var(--as-text-2); padding: 20px !important; }
  .atw-cell-muted { margin-top: 0; color: var(--as-text-2); font-size: 0.8rem; }

  /* Overview tab */
  .atw-kv-table th {
    background: var(--as-surface-2);
    color: var(--as-text-2);
    font-size: 0.72rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    width: 160px;
    white-space: nowrap;
  }
  .atw-kv-table td { color: var(--as-text); font-weight: 500; }

  .atw-controls { margin-top: 16px; display: flex; flex-direction: column; gap: 10px; }
  .atw-seat-row {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
  }
  .atw-control-label {
    font-size: 0.8rem;
    font-weight: 600;
    color: var(--as-text-2);
    white-space: nowrap;
  }
  .atw-seat-input { max-width: 120px; }
  .atw-action-row { display: flex; gap: 8px; flex-wrap: wrap; }

  /* Licenses tab */
  .atw-stat-strip {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 12px;
    flex-wrap: wrap;
  }
  .atw-stat {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 14px;
    border: 1px solid var(--as-border);
    border-radius: 4px;
    background: var(--as-surface-2);
    font-size: 0.8rem;
  }
  .atw-stat span { color: var(--as-text-2); }
  .atw-stat strong { color: var(--as-text); font-weight: 700; }
  .atw-refresh-btn { margin-left: auto; }
  .atw-danger-action { color: var(--as-danger) !important; border-color: rgba(164,38,44,.25) !important; }
  .atw-danger-action:hover { background: var(--as-danger-bg) !important; }

  /* Billing tab */
  .atw-grant-form { margin-bottom: 0; }
  .atw-grant-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 12px;
  }

  /* Shared form helpers */
  .admin-form-field { display: flex; flex-direction: column; gap: 4px; }
  .admin-form-label { font-size: 12px; font-weight: 600; color: var(--as-text); }

  /* Users tab - reset password modal */
  .atw-modal-backdrop {
    position: fixed; inset: 0; z-index: 1000;
    background: rgba(0,0,0,.35);
    display: flex; align-items: center; justify-content: center; padding: 16px;
  }
  .atw-modal {
    width: 100%; max-width: 380px;
    background: var(--as-surface); border-radius: var(--as-r);
    box-shadow: 0 8px 32px rgba(0,0,0,.18);
    display: flex; flex-direction: column; overflow: hidden;
  }
  .atw-modal-head {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 10px; padding: 16px 18px 12px;
    border-bottom: 1px solid var(--as-border);
  }
  .atw-modal-title { font-size: 14px; font-weight: 600; color: var(--as-text); }
  .atw-modal-sub { font-size: 12px; color: var(--as-text-dim); margin-top: 2px; }
  .atw-modal-body { padding: 16px 18px; }
  .atw-modal-foot {
    display: flex; justify-content: flex-end; gap: 8px;
    padding: 12px 18px; border-top: 1px solid var(--as-border);
  }
  .admin-btn-icon {
    display: inline-flex; align-items: center; justify-content: center;
    width: 28px; height: 28px; border: none; border-radius: var(--as-r);
    background: transparent; color: var(--as-text-dim); cursor: pointer;
    transition: background .12s;
  }
  .admin-btn-icon:hover { background: var(--as-bg); color: var(--as-text); }

  /* Enterprise Quote tab */
  .atw-quote-wrap { max-width: 600px; }
  .atw-quote-head-row {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 12px; margin-bottom: 16px;
  }
  .atw-quote-desc { font-size: 13px; color: var(--as-text-dim); margin: 0; }
  .atw-quote-grid {
    display: grid; grid-template-columns: 1fr 1fr 80px; gap: 10px; margin-bottom: 12px;
  }
  .atw-currency-input { text-transform: uppercase; letter-spacing: 1px; }
  .atw-quote-info {
    font-size: 12px; color: var(--as-text-dim);
    background: var(--as-bg); border: 1px solid var(--as-border);
    border-radius: var(--as-r); padding: 6px 12px; margin-bottom: 8px;
    display: inline-flex; align-items: center; gap: 4px;
  }
  .atw-quote-meta-dim { color: var(--as-text-dim); }
  .atw-quote-toggle-row {
    display: inline-flex; align-items: center; gap: 8px;
    font-size: 13px; color: var(--as-text); cursor: pointer; user-select: none;
    margin-bottom: 2px;
  }
  .atw-quote-note { resize: vertical; min-height: 52px; }

  /* Detail page */
  .atw-back-btn {
    display: inline-flex; align-items: center; gap: 6px;
    font-size: 13px; font-weight: 500; color: var(--as-text-2);
    background: none; border: none; padding: 0; cursor: pointer;
    margin-bottom: 12px;
    transition: color .12s;
  }
  .atw-back-btn:hover { color: var(--as-primary); }
  .atw-detail-page-header {
    display: flex; align-items: flex-start; justify-content: space-between;
    gap: 16px; flex-wrap: wrap; margin-bottom: 6px;
  }
  .atw-detail-page-title {
    display: flex; align-items: center; gap: 10px;
    font-size: 1.35rem; font-weight: 700; color: var(--as-text); margin: 0 0 6px;
  }
  .atw-detail-meta {
    font-size: 13px; color: var(--as-text-2); display: flex; align-items: center; gap: 10px; flex-wrap: wrap;
  }
  .atw-page-tabbar {
    display: flex; gap: 2px; margin: 18px 0 0;
    border-bottom: 2px solid var(--as-border);
    overflow-x: auto;
  }
  .atw-page-tab {
    display: inline-flex; align-items: center; gap: 7px;
    padding: 9px 18px; font-size: 13px; font-weight: 500;
    color: var(--as-text-2); background: none; border: none;
    border-bottom: 2px solid transparent; margin-bottom: -2px;
    cursor: pointer; white-space: nowrap;
    transition: color .12s, border-color .12s;
  }
  .atw-page-tab:hover { color: var(--as-text); }
  .atw-page-tab.is-active { color: var(--as-primary); border-bottom-color: var(--as-primary); font-weight: 600; }
  .atw-page-body { padding-top: 20px; }
`;

const TABS = [
  { id: "overview",  label: "Overview",        icon: <Building2 size={14} /> },
  { id: "licensing", label: "Licenses",         icon: <Users size={14} /> },
  { id: "billing",   label: "Billing",          icon: <CreditCard size={14} /> },
  { id: "users",     label: "Users",            icon: <KeyRound size={14} /> },
  { id: "quote",     label: "Enterprise Quote", icon: <Quote size={14} /> },
];

/* ════════════════════════════════════════
   Tenant detail page (full-screen)
   ════════════════════════════════════════ */
function TenantDetailPage({ tenantId }) {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/admin/tenants");
      const d = await res.json().catch(() => []);
      if (!res.ok) throw new Error(d?.message || "Failed to load tenants");
      const found = (Array.isArray(d) ? d : []).find(t => t.id === tenantId) || null;
      if (!found) { navigate("/admin/tenants", { replace: true }); return; }
      setTenant(found);
    } catch (e) { toast.error(e?.message || "Failed to load tenant"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, [tenantId]);

  const toggleActive = async (t) => {
    const res = await apiPost(`/api/admin/tenants/${encodeURIComponent(t.id)}/toggle`, { isActive: !t.isActive });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(d?.message || "Failed"); return; }
    toast.success(d?.message || "Updated"); void load();
  };

  const deleteTenant = async (t) => {
    if (!window.confirm(`Delete ${t.name} and all linked data? This cannot be undone.`)) return;
    const res = await apiDelete(`/api/admin/tenants/${encodeURIComponent(t.id)}`);
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { toast.error(d?.message || "Delete failed"); return; }
    toast.success(d?.message || "Deleted");
    navigate("/admin/tenants", { replace: true });
  };

  if (loading) {
    return (
      <div className="admin-workspace">
        <div className="admin-empty-state">Loading tenant…</div>
        <style>{ATW_STYLES}</style>
      </div>
    );
  }

  if (!tenant) return null;

  return (
    <div className="admin-workspace">
      {/* Back button */}
      <button className="atw-back-btn" onClick={() => navigate("/admin/tenants")}>
        <ChevronDown size={14} style={{ transform: "rotate(90deg)" }} />
        All Tenants
      </button>

      {/* Page header */}
      <div className="atw-detail-page-header">
        <div>
          <h1 className="atw-detail-page-title">
            <Building2 size={20} />
            {tenant.name || "Unnamed Tenant"}
          </h1>
          <div className="atw-detail-meta">
            <span>{tenant.ownerEmail || "No owner email"}</span>
            <span className={`admin-status ${tenant.isActive ? "is-active" : "is-disabled"}`} style={{ fontSize: "0.75rem" }}>
              {tenant.isActive ? "Active" : "Disabled"}
            </span>
            {tenant.activePlanName && (
              <span className="admin-chip">{tenant.activePlanName}</span>
            )}
          </div>
        </div>
        <button className="gc-icon-btn" data-tip="Refresh" onClick={load}>
          <RefreshCw size={14} />
        </button>
      </div>

      {/* Tab bar */}
      <div className="atw-page-tabbar">
        {TABS.map(tab => (
          <button
            key={tab.id}
            type="button"
            className={`atw-page-tab${activeTab === tab.id ? " is-active" : ""}`}
            onClick={() => {
              if (tab.id === "billing") {
                navigate(`/admin/tenants/${tenant.id}/billing`, { state: { tenantName: tenant.name } });
              } else {
                setActiveTab(tab.id);
              }
            }}
          >
            {tab.icon} {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="atw-page-body">
        {activeTab === "overview"  && <OverviewTab tenant={tenant} onToggle={toggleActive} onDelete={deleteTenant} onRefresh={load} />}
        {activeTab === "licensing" && <LicensesTab tenantId={tenant.id} />}
        {activeTab === "users"     && <UsersTab    tenantId={tenant.id} />}
        {activeTab === "quote"     && <EnterpriseQuoteTab tenantId={tenant.id} />}
      </div>

      <style>{ATW_STYLES}</style>
    </div>
  );
}

/* ════════════════════════════════════════
   Tenant list page
   ════════════════════════════════════════ */
function TenantListPage() {
  const [tenants, setTenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const navigate = useNavigate();

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiGet("/api/admin/tenants");
      const d = await res.json().catch(() => []);
      if (!res.ok) throw new Error(d?.message || "Failed to load tenants");
      setTenants(Array.isArray(d) ? d : []);
    } catch (e) { toast.error(e?.message || "Failed to load tenants"); }
    finally { setLoading(false); }
  };

  useEffect(() => { void load(); }, []);

  const filteredTenants = useMemo(() => {
    const q = normalizeText(search).toLowerCase();
    return tenants.filter(t => {
      const s = statusFilter === "all"
        || (statusFilter === "active"   &&  t?.isActive)
        || (statusFilter === "disabled" && !t?.isActive);
      const m = !q || [t?.name, t?.ownerEmail, t?.enrollmentKey].some(v => normalizeText(v).toLowerCase().includes(q));
      return s && m;
    });
  }, [tenants, search, statusFilter]);

  const active = tenants.filter(t => t?.isActive).length;
  const disabled = tenants.length - active;

  return (
    <div className="admin-workspace">
      {/* Page header */}
      <div className="admin-shell-header">
        <div className="admin-shell-copy">
          <span className="admin-shell-eyebrow">Tenant Workspace</span>
          <h1 className="admin-shell-title">Tenants</h1>
          <p className="admin-shell-subtitle">Manage workspaces, licensing, and billing for every tenant.</p>
        </div>
        <div className="admin-shell-actions">
          <button className="gc-icon-btn" data-tip="Refresh" onClick={load}>
          <RefreshCw size={14} />
        </button>
        </div>
      </div>

      {/* Summary pills */}
      <div className="atw-pills">
        <div className="atw-pill">
          <span>Total</span><strong>{tenants.length}</strong>
        </div>
        <div className="atw-pill atw-pill--success">
          <span>Active</span><strong>{active}</strong>
        </div>
        <div className="atw-pill atw-pill--danger">
          <span>Disabled</span><strong>{disabled}</strong>
        </div>
      </div>

      {/* Filters toolbar */}
      <div className="admin-toolbar">
        <label className="admin-search" style={{ flex: "1 1 380px" }}>
          <Search size={14} />
          <input
            type="search"
            placeholder="Search by tenant name, owner email, or enrollment key…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </label>
        <select className="admin-filter" value={statusFilter} onChange={e => setStatusFilter(e.target.value)}>
          <option value="all">All statuses</option>
          <option value="active">Active only</option>
          <option value="disabled">Disabled only</option>
        </select>
        <span className="admin-panel-kicker">{filteredTenants.length} of {tenants.length}</span>
      </div>

      {/* Tenants table */}
      {loading ? (
        <div className="admin-empty-state">Loading tenants…</div>
      ) : filteredTenants.length === 0 ? (
        <div className="admin-empty-state">No tenants match the current filters.</div>
      ) : (
        <div className="admin-table-wrap">
          <table className="admin-table atw-main-table">
            <thead>
              <tr>
                <th>Tenant Name</th>
                <th>Owner Email</th>
                <th>Enrollment Key</th>
                <th>Status</th>
                <th>Devices</th>
                <th>Seat Cap</th>
                <th>Active Plan</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredTenants.map(t => (
                <tr key={t.id} style={{ cursor: "pointer" }} onClick={() => navigate(`/admin/tenants/${t.id}`)}>
                  <td><div className="admin-cell-title">{t.name || "Unnamed"}</div></td>
                  <td><div className="admin-cell-subtitle" style={{ marginTop: 0 }}>{t.ownerEmail || "-"}</div></td>
                  <td>
                    <span className="admin-monospace" style={{ fontSize: "0.78rem" }}>
                      {t.enrollmentKey ? t.enrollmentKey.slice(0, 16) + "…" : "-"}
                    </span>
                  </td>
                  <td>
                    <span className={`admin-status ${t.isActive ? "is-active" : "is-disabled"}`}>
                      {t.isActive ? "Active" : "Disabled"}
                    </span>
                  </td>
                  <td><div className="admin-cell-title">{Number(t.deviceCount || 0)}</div></td>
                  <td><div className="admin-cell-title">{t.maxSeats === null ? "∞" : Number(t.maxSeats || 5)}</div></td>
                  <td>
                    {t.activePlanName
                      ? <span className="admin-chip">{t.activePlanName}</span>
                      : <span style={{ color: "var(--as-text-3)", fontSize: "0.8rem" }}>—</span>
                    }
                  </td>
                  <td onClick={e => e.stopPropagation()}>
                    <div className="admin-actions">
                      <button className="admin-inline-action" onClick={() => navigate(`/admin/tenants/${t.id}`)}>
                        Manage
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <style>{ATW_STYLES}</style>
    </div>
  );
}

/* ════════════════════════════════════════
   Main export — routes to list or detail
   ════════════════════════════════════════ */
export default function AdminTenantWorkspace() {
  const { tenantId: routeTenantId } = useParams();
  return routeTenantId
    ? <TenantDetailPage tenantId={routeTenantId} />
    : <TenantListPage />;
}
